import TrackedVideo from "../components/TrackedVideo";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

function normalizeText(text) {
  return String(text || "").replace(/^\uFEFF/, "").normalize("NFC").trim();
}

function stripExtension(filename) {
  return filename.replace(/\.[^/.]+$/, "").replace(/_trim$/i, "");
}

const segmentAliases = {
  "dubbel med skruv": "double with twist",
  "double with twist": "double with twist"
};

function canonicalizeSegment(segment) {
  const normalized = normalizeText(segment).toLowerCase();
  if (/^\d+$/.test(normalized)) {
    return String(parseInt(normalized, 10));
  }

  return segmentAliases[normalized] || normalized;
}

function pathsMatchWithAliases(leftPath, rightPath) {
  const leftSegments = leftPath.split("/").filter(Boolean).map(canonicalizeSegment);
  const rightSegments = rightPath.split("/").filter(Boolean).map(canonicalizeSegment);

  if (leftSegments.length !== rightSegments.length) {
    return false;
  }

  return leftSegments.every((segment, index) => segment === rightSegments[index]);
}

function getMatchingMetadataEntry(metadata, clipPath) {
  const directMatch = metadata[clipPath];
  if (directMatch) {
    return directMatch;
  }

  return Object.entries(metadata).find(([candidatePath]) => (
    candidatePath === clipPath || pathsMatchWithAliases(candidatePath, clipPath)
  ))?.[1] || {};
}

function isDirectPathChild(path, parentPath) {
  if (parentPath === "") {
    return path.startsWith("/");
  }

  return path === parentPath || path.startsWith(`${parentPath}/`);
}

function getSubfolders(paths, targetPath, depth) {
  const segmentIndex = depth + 1;

  if (targetPath === "") {
    return Array.from(
      new Set(paths.map((item) => item.split("/")[1]).filter(Boolean))
    );
  }

  return Array.from(
    new Set(
      paths
        .filter((item) => isDirectPathChild(item, targetPath))
        .map((item) => item.split("/")[segmentIndex])
        .filter(Boolean)
    )
  );
}

function isLeafFolder(paths, targetPath, depth) {
  if (!paths.includes(targetPath)) {
    return false;
  }

  return getSubfolders(paths, targetPath, depth).length === 0;
}

function getFolderPath(parentPath, folder) {
  return `${parentPath}/${folder}`;
}

function getFolderDisplayName(folderMetadata, folderPath, fallbackName) {
  return folderMetadata[folderPath]?.displayName || fallbackName;
}

function VideoMetadataAdmin() {
  const [bucketPaths, setBucketPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState({
    path: "",
    subfolders: [],
    depth: 0,
    videos: []
  });
  const [metadata, setMetadata] = useState({});
  const [folderMetadata, setFolderMetadata] = useState({});
  const [csvTitles, setCsvTitles] = useState({});
  const [drafts, setDrafts] = useState({});
  const [folderDrafts, setFolderDrafts] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [savingClipPath, setSavingClipPath] = useState("");
  const [savingFolderPath, setSavingFolderPath] = useState("");
  const [videoUrls, setVideoUrls] = useState({});

  async function fetchStructure() {
    try {
      const response = await axios.get(`${API_URL}/structure/structure`, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });

      setBucketPaths(response.data || []);
    } catch (error) {
      setErrorMessage("Kunde inte hämta videostrukturen");
    }
  }

  async function fetchMetadata() {
    try {
      const response = await axios.get(`${API_URL}/video-metadata/all`, {
        headers: {
          token: localStorage.getItem("token")
        }
      });

      setMetadata(response.data || {});
    } catch (error) {
      setErrorMessage("Kunde inte hämta videometadata");
    }
  }

  async function fetchFolderMetadata() {
    try {
      const response = await axios.get(`${API_URL}/folder-metadata/all`, {
        headers: {
          token: localStorage.getItem("token")
        }
      });

      setFolderMetadata(response.data || {});
    } catch (error) {
      setErrorMessage("Kunde inte hämta mappmetadata");
    }
  }

  async function fetchCsvTitles() {
    try {
      const response = await fetch("/titles.csv");
      const text = normalizeText(await response.text());
      const rows = text
        .split(/\r?\n/)
        .map((row) => row.trim())
        .filter(Boolean);

      const parsedTitles = {};
      rows.forEach((row) => {
        const commaIndex = row.indexOf(",");
        if (commaIndex === -1) {
          return;
        }

        const clipPath = normalizeText(row.slice(0, commaIndex));
        const title = normalizeText(row.slice(commaIndex + 1));
        if (clipPath) {
          parsedTitles[clipPath] = title;
        }
      });

      setCsvTitles(parsedTitles);
    } catch (error) {
      console.error("Error fetching CSV titles:", error);
    }
  }

  function bucketRequest(queryPath) {
    return axios.get(`${API_URL}/bucket/list-folder`, {
      headers: {
        "Content-Type": "application/json",
        token: localStorage.getItem("token")
      },
      params: {
        path: encodeURIComponent(queryPath)
      }
    }).then((response) => response.data);
  }

  async function navigateToPath(targetPath) {
    if (targetPath === currentPath.path) {
      return;
    }

    if (targetPath === "") {
      setCurrentPath({
        path: "",
        subfolders: getSubfolders(bucketPaths, "", 0),
        depth: 0,
        videos: []
      });
      return;
    }

    const depth = targetPath.split("/").filter(Boolean).length;
    const leafFolder = isLeafFolder(bucketPaths, targetPath, depth);
    let videos = [];

    if (leafFolder) {
      const queryPath = targetPath.slice(1);
      videos = await bucketRequest(queryPath);
      videos = videos.filter((video) => video.includes(".mp4"));
    }

    setCurrentPath({
      path: targetPath,
      subfolders: !leafFolder ? getSubfolders(bucketPaths, targetPath, depth) : [],
      depth,
      videos
    });
  }

  async function handleClickForward(folder) {
    const nextPath = `${currentPath.path}/${folder}`;
    const depth = currentPath.depth + 1;
    const leafFolder = isLeafFolder(bucketPaths, nextPath, depth);
    let videos = [];

    if (leafFolder) {
      const queryPath = nextPath.slice(1);
      videos = await bucketRequest(queryPath);
      videos = videos.filter((video) => video.includes(".mp4"));
    }

    setCurrentPath({
      path: nextPath,
      subfolders: !leafFolder ? getSubfolders(bucketPaths, nextPath, depth) : [],
      depth,
      videos
    });
  }

  async function saveFolderMetadata(folderPath, fallbackName) {
    const displayName = folderDrafts[folderPath] ?? folderMetadata[folderPath]?.displayName ?? "";

    try {
      setSavingFolderPath(folderPath);
      const response = await axios.patch(`${API_URL}/folder-metadata/item`,
        {
          folderPath,
          displayName
        },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      const savedFolderPath = response.data.folderPath || folderPath;
      const savedDisplayName = response.data.displayName || "";

      setFolderMetadata((previous) => {
        const next = { ...previous };
        delete next[folderPath];
        next[savedFolderPath] = { displayName: savedDisplayName };
        return next;
      });
      setFolderDrafts((previous) => {
        const next = { ...previous };
        delete next[folderPath];
        next[savedFolderPath] = savedDisplayName;
        return next;
      });
      setStatusMessage(`Mappnamnet sparades: ${savedDisplayName || fallbackName}`);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte spara mappnamnet");
      setStatusMessage("");
    } finally {
      setSavingFolderPath("");
    }
  }

  function handleClickBack() {
    if (currentPath.path === "") {
      return;
    }

    const nextPath = currentPath.path.split("/").slice(0, -1).join("/");
    const nextDepth = currentPath.depth - 1;
    setCurrentPath({
      path: nextPath,
      subfolders: getSubfolders(bucketPaths, nextPath, nextDepth),
      depth: nextDepth,
      videos: []
    });
  }

  async function saveClipMetadata(clipPath) {
    const draft = drafts[clipPath];
    if (!draft) {
      return;
    }

    try {
      setSavingClipPath(clipPath);
      const response = await axios.patch(`${API_URL}/video-metadata/item`,
        {
          clipPath,
          title: draft.title,
          description: draft.description,
          hidden: Boolean(draft.hidden)
        },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      const savedClipPath = response.data.clipPath || clipPath;
      const savedMetadata = {
        title: response.data.title,
        description: response.data.description || "",
        hidden: Boolean(response.data.hidden)
      };

      setMetadata((previous) => {
        const next = { ...previous };
        delete next[clipPath];
        next[savedClipPath] = savedMetadata;
        return next;
      });
      setStatusMessage("Videometadata sparades.");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte spara videometadata");
      setStatusMessage("");
    } finally {
      setSavingClipPath("");
    }
  }

  async function loadVideoPreview(clipPath, videoFilename) {
    if (videoUrls[clipPath]) {
      return;
    }

    try {
      const filePath = `${currentPath.path.slice(1)}/${videoFilename}`;
      const encodedFilePath = encodeURIComponent(filePath);

      const response = await axios.get(`${API_URL}/bucket/presigned-url-file`, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        },
        params: {
          file: encodedFilePath
        }
      });

      setVideoUrls((previous) => ({
        ...previous,
        [clipPath]: response.data
      }));
    } catch (error) {
      setErrorMessage("Kunde inte hämta videoförhandsvisning");
    }
  }

  async function resetClipMetadataToCsv(clipPath) {
    const csvTitleEntry = Object.entries(csvTitles).find(([candidatePath]) => (
      candidatePath === clipPath || pathsMatchWithAliases(candidatePath, clipPath)
    ));
    const originalTitle = csvTitleEntry ? csvTitleEntry[1] : stripExtension(clipPath.split("/").pop() || "");

    try {
      setSavingClipPath(clipPath);
      const response = await axios.patch(`${API_URL}/video-metadata/item`,
        {
          clipPath,
          title: originalTitle,
          description: "",
          hidden: false
        },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      const savedClipPath = response.data.clipPath || clipPath;
      const savedMetadata = {
        title: response.data.title,
        description: response.data.description || "",
        hidden: Boolean(response.data.hidden)
      };

      setMetadata((previous) => {
        const next = { ...previous };
        delete next[clipPath];
        next[savedClipPath] = savedMetadata;
        return next;
      });
      setDrafts((previous) => {
        const next = { ...previous };
        delete next[clipPath];
        next[savedClipPath] = savedMetadata;
        return next;
      });
      setStatusMessage("Videometadata återställdes till originaltexten.");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte återställa videometadata");
      setStatusMessage("");
    } finally {
      setSavingClipPath("");
    }
  }

  useEffect(() => {
    fetchStructure();
    fetchMetadata();
    fetchFolderMetadata();
    fetchCsvTitles();
  }, []);

  useEffect(() => {
    if (bucketPaths.length === 0 || currentPath.path !== "") {
      return;
    }

    setCurrentPath((previous) => ({
      ...previous,
      subfolders: getSubfolders(bucketPaths, "", 0)
    }));
  }, [bucketPaths, currentPath.path]);

  const breadcrumbParts = currentPath.path ? currentPath.path.split("/").filter(Boolean) : [];

  const visibleFolders = useMemo(() => {
    return currentPath.subfolders.map((folder) => {
      const folderPath = getFolderPath(currentPath.path, folder);
      const displayName = getFolderDisplayName(folderMetadata, folderPath, folder);
      const draftDisplayName = folderDrafts[folderPath] ?? folderMetadata[folderPath]?.displayName ?? "";

      return {
        folder,
        folderPath,
        displayName,
        draftDisplayName
      };
    });
  }, [currentPath.path, currentPath.subfolders, folderDrafts, folderMetadata]);

  const visibleVideos = useMemo(() => {
    return currentPath.videos.map((video) => {
      const clipPath = `${currentPath.path}/${stripExtension(video)}`;
      const savedMetadata = getMatchingMetadataEntry(metadata, clipPath);
      const csvTitleEntry = Object.entries(csvTitles).find(([candidatePath]) => (
        candidatePath === clipPath || pathsMatchWithAliases(candidatePath, clipPath)
      ));
      const fallbackCsvTitle = csvTitleEntry ? csvTitleEntry[1] : "";
      const draft = drafts[clipPath] || {
        title: savedMetadata.title || fallbackCsvTitle || stripExtension(video),
        description: savedMetadata.description || "",
        hidden: Boolean(savedMetadata.hidden)
      };

      return {
        video,
        clipPath,
        draft
      };
    });
  }, [csvTitles, currentPath.path, currentPath.videos, drafts, metadata]);

  return (
    <div className="flex flex-col items-center px-3">
      <div className="w-full max-w-5xl py-4">
        <div className="rounded-[28px] bg-gradient-to-br from-slate-50 via-white to-sky-50 p-5 shadow-sm ring-1 ring-slate-200/80">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Administration</p>
          <h1 className="mt-2 text-lg font-bold text-slate-800">Redigera video- och mappmetadata</h1>
          <p className="mt-2 text-sm text-slate-600">
            Navigera i videoträdstrukturen och redigera visningsnamn för mappar samt namn, beskrivning och synlighet för varje videoklipp.
          </p>

          {statusMessage && (
            <div className="mt-4 rounded-2xl border border-green-300 bg-green-50 px-5 py-3 text-sm text-green-800">
              {statusMessage}
            </div>
          )}
          {errorMessage && (
            <div className="mt-4 rounded-2xl border border-red-300 bg-red-50 px-5 py-3 text-sm text-red-800">
              {errorMessage}
            </div>
          )}

          <div className="mt-5 rounded-[24px] border border-slate-200 bg-white/85 p-4">
            <div className="text-sm font-semibold text-slate-800">Videobibliotek</div>
            <div className="mt-2 flex flex-wrap items-center gap-y-2 text-sm text-slate-500">
              <button
                className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 font-medium text-sky-700 transition-colors hover:bg-sky-100 hover:text-sky-900"
                onClick={() => navigateToPath("")}
              >
                Start
              </button>
              {breadcrumbParts.map((part, index) => {
                const targetPath = `/${breadcrumbParts.slice(0, index + 1).join("/")}`;
                const isCurrent = index === breadcrumbParts.length - 1;
                const displayName = getFolderDisplayName(folderMetadata, targetPath, part);

                return (
                  <span key={targetPath}>
                    <span className="mx-2 inline-flex items-center text-slate-400" aria-hidden="true">
                      <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 3.5L10.5 8L6 12.5L7.4 13.9L13.3 8L7.4 2.1L6 3.5Z" />
                      </svg>
                    </span>
                    {isCurrent ? (
                      <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 font-medium text-slate-700">
                        {displayName}
                      </span>
                    ) : (
                      <button
                        className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 font-medium text-sky-700 transition-colors hover:bg-sky-100 hover:text-sky-900"
                        onClick={() => navigateToPath(targetPath)}
                      >
                        {displayName}
                      </button>
                    )}
                  </span>
                );
              })}
            </div>

            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <button
                className="flex-1 rounded-[20px] border border-red-200 bg-gradient-to-r from-red-500 to-rose-500 px-5 py-3 font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                onClick={handleClickBack}
              >
                Föregående nivå
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
            <div className="rounded-[24px] border border-slate-200 bg-white/85 p-4">
              <p className="text-sm font-semibold text-slate-700">Mappar</p>
              <div className="mt-4 space-y-3">
                {visibleFolders.length === 0 && (
                  <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                    Inga undermappar på denna nivå.
                  </div>
                )}
                {visibleFolders.map(({ folder, folderPath, displayName, draftDisplayName }) => (
                  <div
                    key={folderPath}
                    className="rounded-[22px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-800">{displayName}</p>
                        <p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-sky-700">
                          {folderPath}
                        </p>
                      </div>
                      <button
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/80 text-sky-700 shadow-sm ring-1 ring-sky-100 transition-colors hover:bg-sky-100"
                        onClick={() => handleClickForward(folder)}
                        title="Öppna mapp"
                        type="button"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M6 3.5L10.5 8L6 12.5L7.4 13.9L13.3 8L7.4 2.1L6 3.5Z" />
                        </svg>
                      </button>
                    </div>
                    <div className="mt-3 grid gap-2">
                      <input
                        value={draftDisplayName}
                        className="rounded-2xl border border-sky-200 bg-white px-4 py-3 text-sm shadow-sm outline-none transition-colors focus:border-sky-400"
                        placeholder={folder}
                        onChange={(event) => setFolderDrafts((previous) => ({
                          ...previous,
                          [folderPath]: event.target.value
                        }))}
                      />
                      <button
                        className="rounded-[18px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => saveFolderMetadata(folderPath, folder)}
                        disabled={savingFolderPath === folderPath}
                        type="button"
                      >
                        {savingFolderPath === folderPath ? "Sparar..." : "Spara mappnamn"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white/85 p-4">
              <p className="text-sm font-semibold text-slate-700">Videoklipp</p>
              <div className="mt-4 space-y-4">
                {visibleVideos.length === 0 && (
                  <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Välj en videomapp för att redigera metadata för klippen i den mappen.
                  </div>
                )}

                {visibleVideos.map(({ video, clipPath, draft }) => (
                  <div key={clipPath} className="rounded-[22px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 p-4 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
                        {clipPath}
                      </div>
                      {draft.hidden && (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">
                          Dolt för användare
                        </span>
                      )}
                    </div>
                    <div className="mt-3 rounded-[20px] border border-slate-200 bg-white/85 p-3">
                      {videoUrls[clipPath] ? (
                        <TrackedVideo clipPath={clipPath} clipTitle={draft.title} controls className="max-h-[320px] w-full rounded-2xl shadow-sm">
                          <source src={videoUrls[clipPath]} />
                        </TrackedVideo>
                      ) : (
                        <button
                          className="w-full rounded-[18px] border border-sky-200 bg-sky-50 px-4 py-6 text-sm font-semibold text-sky-700 transition-colors hover:bg-sky-100"
                          onClick={() => loadVideoPreview(clipPath, video)}
                        >
                          Visa förhandsvisning
                        </button>
                      )}
                    </div>
                    <div className="mt-3 grid gap-3">
                      <input
                        value={draft.title}
                        className="rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
                        placeholder={stripExtension(video)}
                        onChange={(event) => setDrafts((previous) => ({
                          ...previous,
                          [clipPath]: {
                            ...draft,
                            title: event.target.value
                          }
                        }))}
                      />
                      <textarea
                        value={draft.description}
                        className="min-h-[120px] rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
                        placeholder="Beskrivning av videoklippet"
                        onChange={(event) => setDrafts((previous) => ({
                          ...previous,
                          [clipPath]: {
                            ...draft,
                            description: event.target.value
                          }
                        }))}
                      />
                      <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          checked={Boolean(draft.hidden)}
                          onChange={(event) => setDrafts((previous) => ({
                            ...previous,
                            [clipPath]: {
                              ...draft,
                              hidden: event.target.checked
                            }
                          }))}
                        />
                        <div>
                          <div className="font-semibold text-slate-800">Dolj klippet</div>
                          <div className="mt-1 text-xs text-slate-500">
                            När detta är ikryssat kan vanliga användare inte välja klippet, och mappar utan synliga klipp döljs automatiskt.
                          </div>
                        </div>
                      </label>
                      <div className="flex justify-end">
                        <button
                          className="mr-2 rounded-[20px] border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => resetClipMetadataToCsv(clipPath)}
                          disabled={savingClipPath === clipPath}
                        >
                          Återställ till CSV
                        </button>
                        <button
                          className="rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => saveClipMetadata(clipPath)}
                          disabled={savingClipPath === clipPath}
                        >
                          {savingClipPath === clipPath ? "Sparar..." : "Spara metadata"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoMetadataAdmin;
